// src/utils/amazon-affiliate.ts
// Builds compliant Amazon Associates *search* links from a car query.
//
// Why search links (and not product cards)? Pulling Amazon product titles/
// images/prices requires the Product Advertising API, which is only granted
// after 3 qualifying sales. Hand-built search links carrying the tag are
// allowed immediately and need no product data — so we render our own button
// text that drops the visitor onto a relevant Amazon search.

/**
 * Master switch for every Amazon affiliate link and CTA on the site. Flipping
 * this to true restores all of them; the static disclosure copy on about.html,
 * privacy.html and random-car-generator.html has to be restored by hand —
 * see docs/amazon-affiliate.md.
 */
export const AFFILIATE_LINKS_ENABLED: boolean = false;

/** Associates tracking ID dedicated to GarageWish (see Associates Central). */
export const AMAZON_TAG = "garagewish-20";

/** Required Amazon Associates disclosure — must appear where links are shown. */
export const AFFILIATE_DISCLOSURE = "As an Amazon Associate we earn from qualifying purchases.";

// Colour words tend to over-narrow model-car results ("white" matches white
// paint, not the car), so we strip them before searching.
const COLOR_WORDS = new Set([
  "white", "black", "red", "blue", "green", "yellow", "silver",
  "grey", "gray", "orange", "purple", "gold", "matte", "metallic",
]);

/**
 * Turn a dream-car search ("White Lamborghini Huracán") into model-car
 * keywords ("Lamborghini Huracán diecast model car").
 */
export function buildModelCarKeywords(query: string): string {
  const stripped = query
    .trim()
    .split(/\s+/)
    .filter((word) => !COLOR_WORDS.has(word.toLowerCase()))
    .join(" ")
    .trim();

  const base = stripped || query.trim();
  return `${base} diecast model car`;
}

/**
 * Build an Amazon search URL for the model car matching a query, tagged with
 * our Associates ID. Returns a US (amazon.com) search link.
 */
export function buildAmazonSearchUrl(query: string): string {
  const params = new URLSearchParams({
    k: buildModelCarKeywords(query),
    tag: AMAZON_TAG,
  });
  return `https://www.amazon.com/s?${params.toString()}`;
}

/**
 * Short, human label for the CTA, e.g. "Lamborghini Huracán" from
 * "White Lamborghini Huracán". Falls back to the raw query.
 */
export function carLabelFromQuery(query: string): string {
  const stripped = query
    .trim()
    .split(/\s+/)
    .filter((word) => !COLOR_WORDS.has(word.toLowerCase()))
    .join(" ")
    .trim();
  return stripped || query.trim();
}
